import { useCallback, useEffect, useMemo, useState } from "react";
import { GroupBlockData } from "../../configMap";
import { RootState, useAppDispatch, useAppSelector } from "../../app";
import useMeasure from "react-use-measure";
import useGroupBlockForTable from "./useGroupBlockForTable";
import useActionAddLine from "./useActionAddLine";
import useSetGroupBlockTabHeightByLines from "./useSetTableControlsHeight";
import { setAutoResizeDisabled } from "../store/AnnotationTableControlsSlice";
import { shallowEqual } from "react-redux";

const useAnnotationTableControls = (
  disabled: boolean,
  multipleLinesGroupBlocks: Array<GroupBlockData>,
) => {
  const dispatch = useAppDispatch();
  const configMapLoading = useAppSelector(
    (state) => state.configMapReducer.loading,
    shallowEqual,
  );

  const selectedGroupBlockId = useAppSelector(
    (state: RootState) =>
      state.annotationTableControlsReducer.selectedGroupBlockId,
    shallowEqual,
  );

  const { isTableControlsResizing } = useAppSelector(
    (state) => state.annotationTableControlsReducer,
    shallowEqual,
  );

  const [refHeightContainer, { height }] = useMeasure();

  const [currentHeight, setCurrentHeight] = useState(height);

  const addGroupBlockAction = useActionAddLine(multipleLinesGroupBlocks);

  const { setTableControlsHeightOnAddLine } =
    useSetGroupBlockTabHeightByLines();

  const selectedGroupBlock = useMemo(() => {
    return multipleLinesGroupBlocks.find(
      (gb) => gb.id === selectedGroupBlockId,
    );
  }, [selectedGroupBlockId, multipleLinesGroupBlocks]);

  const selectedGroupBlockForTable = useGroupBlockForTable(
    selectedGroupBlock,
    disabled,
  );

  const addGroupBlockLineAction = useCallback(
    (groupBlock: GroupBlockData, index: number) => {
      addGroupBlockAction(groupBlock, index);
      setTableControlsHeightOnAddLine();
    },
    [addGroupBlockAction, setTableControlsHeightOnAddLine],
  );

  // Avoid setting the actual height during table control resizing to prevent excessive rendering.
  // Mirroring the measured height while rendering rather than from an effect
  // keeps the exact same guard and settles on the same value, one paint earlier.
  if (!isTableControlsResizing && currentHeight !== height) {
    setCurrentHeight(height);
  }

  useEffect(() => {
    return () => {
      dispatch(setAutoResizeDisabled(false));
    };
  }, [dispatch]);

  return {
    selectedGroupBlockForTable,
    selectedGroupBlock,
    addGroupBlockLineAction,
    configMapLoading,
    refHeightContainer,
    currentHeight,
  };
};

export default useAnnotationTableControls;
